/**
 * Link utilities for TinyClub web app.
 * Re-exports shared pure utilities and provides web-specific link handling.
 */

import { useNavigate } from "@tanstack/react-router";

// Re-export all pure parsing utilities from shared
export {
    URL_REGEX,
    EMAIL_REGEX,
    URL_OR_EMAIL_REGEX,
    cleanUrlMatch,
    isGifUrl,
    countUrls,
    extractAllUrls,
    extractFirstUrl,
    splitTextWithFirstUrl,
    splitTextWithAllUrls,
    type TextPart,
} from "@tinyclub/shared/utils";

// Import EMAIL_REGEX for use in handleLinkPress
import { EMAIL_REGEX } from "@tinyclub/shared/utils";

/**
 * Opens a URL or email in the appropriate way for web
 * @param url - The URL or email address to open
 * @param navigate - Optional TanStack Router navigate function for internal navigation
 */
export function handleLinkPress(
    url: string,
    navigate?: ReturnType<typeof useNavigate>
): void {
    try {
        // Check if this is an email address
        const isEmail = EMAIL_REGEX.test(url);
        EMAIL_REGEX.lastIndex = 0; // Reset regex state

        if (isEmail) {
            // Handle email addresses with mailto:
            window.location.href = `mailto:${url}`;
            return;
        }

        // Add https:// if no protocol is specified
        let fullUrl = url;
        if (!fullUrl.match(/^[a-zA-Z]+:\/\//)) {
            fullUrl = `https://${fullUrl}`;
        }

        // Check if this is a tiny.club URL
        const urlObj = new URL(fullUrl);
        const isTinyClubUrl =
            urlObj.hostname === "tiny.club" || urlObj.hostname === "www.tiny.club";

        if (isTinyClubUrl && navigate) {
            // Parse tiny.club URLs and navigate internally
            const pathname = urlObj.pathname;

            // Handle event links: /e/{code} and /events/{identifier}
            const eventMatch = pathname.match(/^\/(?:e|events)\/([^\/]+)$/);
            if (eventMatch) {
                const identifier = eventMatch[1];
                navigate({ to: "/events/$identifier", params: { identifier } });
                return;
            }

            // Handle club links: /clubs/{identifier} or /c/{identifier}
            const clubMatch = pathname.match(/^\/(?:clubs|c)\/([^\/]+)$/);
            if (clubMatch) {
                const identifier = clubMatch[1];
                navigate({ to: "/clubs/$identifier", params: { identifier } });
                return;
            }

            // Handle join links: /join/{inviteCode} (legacy)
            const joinMatch = pathname.match(/^\/join\/([^\/]+)$/);
            if (joinMatch) {
                const inviteCode = joinMatch[1];
                navigate({ to: "/clubs/$identifier", params: { identifier: inviteCode } });
                return;
            }

            // Handle post links: /p/{postId}
            const postMatch = pathname.match(/^\/p\/([^\/]+)$/);
            if (postMatch) {
                const postId = postMatch[1];
                navigate({ to: "/p/$postId", params: { postId } });
                return;
            }

            // Handle canonical post links: /clubs/{clubId}/posts/{postId}
            const clubPostMatch = pathname.match(/^\/clubs\/([^\/]+)\/posts\/([^\/]+)$/);
            if (clubPostMatch) {
                const clubId = clubPostMatch[1];
                const postId = clubPostMatch[2];
                navigate({ to: "/clubs/$clubId/posts/$postId", params: { clubId, postId } });
                return;
            }

            // For other tiny.club URLs, open in new tab
            window.open(fullUrl, "_blank", "noopener,noreferrer");
        } else {
            // External URL - open in new tab
            window.open(fullUrl, "_blank", "noopener,noreferrer");
        }
    } catch (error) {
        console.warn("Failed to open URL:", error);
        // Fallback: try to open as-is
        window.open(url, "_blank", "noopener,noreferrer");
    }
}

/**
 * Hook-friendly version that returns a handler with navigate pre-bound
 */
export function useHandleLinkPress() {
    const navigate = useNavigate();
    return (url: string) => handleLinkPress(url, navigate);
}
